import queryString from 'querystring'

import { useRouter } from 'next/router'
import Link from 'next/link'
import pickBy from 'lodash/pickBy'
import identity from 'lodash/identity'
import { logEvent } from 'utils/analytics'
import React, { useEffect } from 'react'
import last from 'lodash/last'
import CateringIcon from 'components/icons/CateringIcon'
import OtherIcon from 'components/icons/OtherIcon'
import {
  BriefcaseIcon as ExperimentalIcon,
  CalendarIcon as OnGoingIcon,
  OfficeBuildingIcon as DropOffIcon,
  UserGroupIcon as EventIcon
} from '@heroicons/react/solid'

type Props = {
  organizerSeoName?: string | null
}

export default function HireOptions({ organizerSeoName = null }: Props) {
  const router = useRouter()
  const { truckId, event, note } = router.query ?? {}

  const params = { truckId, event, note }
  const options = [
    {
      title: 'Catering',
      event: 'catering',
      text:
        "I'd like to hire a food truck to cater my event(s) and myself or another host will be paying for all of the meals.",
      href: organizerSeoName
        ? `/catering/by/${organizerSeoName}/catering/form?${queryString.stringify(
            pickBy(params, identity)
          )}`
        : `/catering/form?${queryString.stringify(pickBy(params, identity))}`,
      showToOrganizer: true,
      icon: CateringIcon
    },
    {
      title: 'Drop Off',
      event: 'catering_drop_off',
      text: (
        <span>
          Myself or another host will be paying for all of the meals. Guests
          submit orders in advance and a vendor drops off the food at a desired
          time and leaves after doing so.
        </span>
      ),
      href: organizerSeoName
        ? `/catering/by/${organizerSeoName}/catering-drop-off/form?${queryString.stringify(
            pickBy(params, identity)
          )}`
        : `/catering-drop-off/form?${queryString.stringify(
            pickBy(params, identity)
          )}`,
      showToOrganizer: organizerSeoName !== 'ezcater',
      icon: DropOffIcon
    },
    {
      title: 'Event',
      form_name: 'event_request',
      text:
        "I'd like to hire a truck for my event(s) but attendees will be paying for their own meals. Examples of events are festivals, fundraisers, etc.",
      href: `/events/form?${queryString.stringify(pickBy(params, identity))}`,
      showToOrganizer: false,
      icon: EventIcon
    },
    {
      title: 'Recurring Service',
      event: 'catering_on_going',
      text:
        "I'd like to hire one or multiple trucks to service my location on a daily, weekly or monthly schedule and attendees will pay for their own meals. Examples could be office buildings, campuses, etc.",
      href: `/catering-form/recurring-events?${queryString.stringify(
        pickBy(params, identity)
      )}`,
      showToOrganizer: false,
      icon: OnGoingIcon
    },
    {
      title: 'Brand Activations',
      event: 'experiential_marketing',
      text:
        'I’d like to hire & custom brand a food truck for my mobile marketing campaign, to provide interactive foodie experiences that attract attention, encourage social sharing, & directly engage potential customers.',
      href: `/experiential-marketing/form${queryString.stringify(
        pickBy(params, identity)
      )}`,
      showToOrganizer: false,
      icon: ExperimentalIcon
    },
    {
      title: 'Other',
      form_name: 'event_other',
      text: "My event doesn't fall under any of the other categories above.",
      href: `/catering-form/other-events?${queryString.stringify(
        pickBy(params, identity)
      )}`,
      showToOrganizer: false,
      icon: OtherIcon
    }
  ]

  const filteredOptions = options.filter((option) =>
    organizerSeoName ? option.showToOrganizer : true
  )

  useEffect(() => {
    if (filteredOptions.length === 1) {
      router.push(
        `${filteredOptions[0].href}${
          last(filteredOptions[0].href) === '?' ? '' : '&'
        }hideBackButton=true`
      )
    }
  }, [filteredOptions, router])

  return (
    <div className="w-full px-4">
      <div className="text-center mb-4 lg:mb-12 text-gray-900">
        <h4 className="font-blockBertholdBQ text-xl lg:text-3xl tracking-wide">
          Select the event type from the list below that best fits your needs:
        </h4>
      </div>
      <div
        className={`md:grid ${
          filteredOptions.length < 3
            ? 'grid-cols-2 max-w-3xl mx-auto'
            : 'grid-cols-3'
        } grid-row-2 gap-x-6 gap-y-6 items-stretch`}
      >
        {filteredOptions.map((option) => (
          <div
            key={option.title}
            className="group flex self-stretch rounded-2xl border border-solid border-orange-300 bg-white shadow-sm lg:px-8 lg:py-6 px-6 py-2 cursor-pointer hover:bg-orange-500 transition-all duration-300 sm:flex sm:justify-between focus:outline-none mb-4"
          >
            <Link
              href={option.href}
              onClick={() => {
                logEvent('catering_selection', {
                  form_name: option.form_name
                })
              }}
            >
              <div>
                <div className="w-full">
                  <div className="flex gap-x-2 lg:flex-col items-end lg:items-start">
                    <option.icon className="text-orange-500 group-hover:text-white transition-all duration-300 pb-0.5 lg:p-0 w-8 h-8 lg:w-12 lg:h-12 object-contain" />
                    <p className="mt-2 font-blockBertholdBQ text-gray-900 text-xl lg::text-2xl tracking-wide">
                      {option.title}
                    </p>
                  </div>
                  <p className="mt-2 text-base text-gray-600 group-hover:text-white transition-all duration-300">
                    {option.text}
                  </p>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  )
}
